// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Container, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  details,
  examPaperTypes,
  examTypes,
  ownership,
} from "../../config/constant";
import Loading from "../../pages/Loading";
import { getExamPaper } from "../../slices/examSlice/examPaper";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/system";
import PracticalExamPaper from "./PracticalExamPaper";
import ErrorOccurred from "../../pages/ErrorOccurred";
import { ScenarioBasedMcqExamPaper } from "./ScenarioBasedMcqExamPaper";
import { McqExamPaper } from "./McqExamPaper";
import { setOngoingExamStatus } from "../../slices/examSlice/ongoingExam";
import ExamAlreadySubmitted from "../../pages/ExamAlreadySubmitted";

const ExamPaper = () => {
  let { examId } = useParams();
  const exam = useAppSelector((state: RootState) => state.selectedExam);
  const examPaperState = useAppSelector((state: RootState) => state.examPaper);
  const authState = useAppSelector((state: RootState) => state.auth);
  const contactState = useAppSelector((state: RootState) => state.contact);
  const dispatch = useAppDispatch();
  const examPaper = examPaperState.response;
  const selectedExam = exam.response[0];
  const [conflict, setConflict] = React.useState(false);

  useEffect(() => {
    window.onpopstate = (e) => {
      window.location.reload();
    };
  });

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleBeforeUnload = (e: {
    preventDefault: () => void;
    returnValue: string;
  }) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? All provided data will be lost.";
    e.returnValue = message;
    return message;
  };

  useEffect(() => {
    if (
      authState.status === "success" &&
      contactState.state === "success" &&
      exam.state === "success"
    ) {
      if (examId) {
        var payload = {
          tokenId: examId,
          examId: selectedExam.id,
          user: {
            id: contactState.response.id,
            firstName: contactState.response.firstName,
            lastName: contactState.response.lastName,
            isAdmin: false,
          },
        };
        dispatch(setOngoingExamStatus("ongoing"));
        dispatch(getExamPaper(payload));
      }
    }
  }, [contactState.state]);

  if (conflict) {
    return <ExamAlreadySubmitted />;
  }

  return (
    <>
      {contactState.backgroundProcess || examPaperState.backgroundProcess ? (
        <Loading message={contactState.backgroundProcessMessage} />
      ) : (
        <>
          {exam.state === "success" && examPaperState.state === "success" ? (
            <Container>
              <Grid container columnSpacing="10">
                <Grid
                  item
                  md={12}
                  xs={12}
                  sx={{
                    mt: 5,
                    pt: 10,
                    pb: 5,
                  }}
                >
                  <Typography component="h1" variant="h4" align="left">
                    {`${
                      selectedExam?.examVisibleType
                        ? selectedExam?.examVisibleType
                        : selectedExam?.examType
                    } - ${selectedExam?.examPaperType}`}
                  </Typography>
                  <Typography
                    mt={2}
                    color={"secondary.main"}
                    variant="body1"
                    align="left"
                    component="p"
                  >
                    {details.startExamGuidelines}
                  </Typography>
                  <Box mt={2}>
                    <Typography
                      mt={2}
                      variant="body2"
                      align="left"
                      component="span"
                    >
                      {`${ownership.para1} `}
                    </Typography>
                    <Typography
                      mt={2}
                      variant="body2"
                      align="left"
                      component="span"
                      sx={{ fontWeight: "bold" }}
                    >
                      {` ${contactState.response.firstName} ${contactState.response.lastName}.  `}
                    </Typography>
                    <Typography
                      mt={2}
                      variant="body2"
                      align="left"
                      component="span"
                    >
                      {` ${ownership.para2}`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  item
                  md={12}
                  sx={{ backgroundColor: "#fcf8e3", padding: 2 }}
                >
                  <Stack direction="row" spacing={2}>
                    <InfoOutlinedIcon color="warning" />
                    <Typography
                      color="#ed6c02"
                      variant="body1"
                      align="left"
                      component="span"
                    >
                      DO NOT refresh or click the Back button on this page.
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item md={12} xs={12} sx={{ mt: 5 }}>
                  {examPaper?.questionType == examPaperTypes.practical && (
                    <>
                      <PracticalExamPaper />
                    </>
                  )}

                  {examPaper?.questionType == examPaperTypes.scenarioBased && (
                    <>
                      <ScenarioBasedMcqExamPaper setConflict={setConflict} />
                    </>
                  )}

                  {examPaper?.questionType == examPaperTypes.mcq && (
                    <>
                      <McqExamPaper setConflict={setConflict} />
                    </>
                  )}
                </Grid>
              </Grid>
            </Container>
          ) : (
            <>
              <ErrorOccurred />
            </>
          )}
        </>
      )}
    </>
  );
};

export default ExamPaper;
